import * as api from '../api/queries';
import { useApi } from './useApi';
import { UseQueryOptions } from '@tanstack/react-query';

import { IBrand } from '../types/brand';
import { addAgentBrandApiQueryKey } from '../api/agentBrand/addAgentBrand';
import {
  agentBrandListQueryKey,
  getAgentBrandsList,
} from '../api/agentBrand/getAgentBrands';
import { toast } from 'react-hot-toast';
import moment from 'moment-timezone';
import { getClientList } from '../api/client/getClientList';
import { clientListQueryKey } from '../api/client/addClient';
import { getRequirementByIdApi } from '../api/brand/getRequirementById';
import { getInvoices, invoicesQueryKey } from '../api/queries';
import {
  getLeaveRequests,
  getLeaveRequestsQueryKey,
} from '../api/leave-requests/getLeaveRequests';
import {
  getAgentLeaveBalance,
  agentLeaveBalanceQueryKey,
} from '../api/leave-requests/getAgentLeaveBalance';

export const getAgentBrand = (
  agentId: string,
  brandId: string,
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>
) => {
  const initialProps = {
    queryKey: [...addAgentBrandApiQueryKey(agentId), brandId],
    queryFn: () => api.getAgentBrandApi(agentId, brandId) || [],
  };

  const finalProps = props ? { ...initialProps, ...props } : initialProps;

  return useApi(finalProps);
};

export const fetchAgentBrandList = (
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>
) => {
  const initialProps = {
    queryKey: agentBrandListQueryKey,
    queryFn: getAgentBrandsList,
  };

  const finalProps = props ? { ...initialProps, ...props } : initialProps;

  return useApi(finalProps);
};

export const fetchAgentList = (
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>
) => {
  const initialProps = {
    queryKey: api.agentListQueryKey,
    queryFn: api.getAgentList,
  };

  const finalProps = props ? { ...initialProps, ...props } : initialProps;

  return useApi(finalProps);
};

export const fetchJobListings = (
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>
) => {
  const initialProps = {
    queryKey: api.jobListingsQueryKey,
    queryFn: api.getJobListings,
  };

  const finalProps = props ? { ...initialProps, ...props } : initialProps;

  return useApi(finalProps);
};

export const fetchJobApplications = (
  params?: {
    startRow: number;
    endRow: number;
    filterModel: any;
    sortModel: any;
    search: string;
    category: string;
  },
  options?: Omit<UseQueryOptions<any, any, any, any>, 'queryKey' | 'queryFn'>
) => {
  const queryKey = ['jobApplications', params];

  const queryFn = () => api.getJobApplications(params as any);

  return useApi({
    queryKey,
    queryFn,
    ...options,
  });
};

export const fetchJobApplicationById = (
  props: Omit<UseQueryOptions, 'queryKey' | 'queryFn'> & {
    id: string;
  }
) => {
  const { id, ...queryProps } = props;

  const initialProps = {
    queryKey: api.jobApplicationByIdQueryKey(id),
    queryFn: () => api.getJobApplicationById(id),
  };

  const finalProps = queryProps
    ? { ...initialProps, ...queryProps }
    : initialProps;

  return useApi(finalProps);
};

export const fetchBrandList = (
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>
) => {
  const initialProps = {
    queryKey: api.brandListQueryKey,
    queryFn: api.getBrandList,
  };

  const finalProps = props ? { ...initialProps, ...props } : initialProps;

  return useApi(finalProps);
};

export const fetchClientList = (
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>
) => {
  const initialProps = {
    queryKey: clientListQueryKey,
    queryFn: getClientList,
  };

  const finalProps = props ? { ...initialProps, ...props } : initialProps;

  return useApi(finalProps);
};

export const getAgentBrandMapKey = ['brandsForAgent'];

export const fetchRequirements = (
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>
) => {
  const initialProps = {
    queryKey: api.requirementsQueryKey,
    queryFn: api.getRequirements,
  };

  const finalProps = props ? { ...initialProps, ...props } : initialProps;

  return useApi(finalProps);
};

export const fetchRequirementById = (
  props: Omit<UseQueryOptions, 'queryKey' | 'queryFn'> & {
    id: string;
  }
) => {
  const { id, ...queryProps } = props;

  const initialProps = {
    queryKey: ['requirement', id],
    queryFn: () => getRequirementByIdApi(id),
  };

  const finalProps = queryProps
    ? { ...initialProps, ...queryProps }
    : initialProps;

  return useApi(finalProps);
};

export const fetchBrandsForAgents = (
  props?: Omit<
    UseQueryOptions<
      { [key: string]: IBrand[] },
      Error,
      { [key: string]: IBrand[] }
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { data: brands, isSuccess: isBrandsSuccess } = fetchBrandList();

  const initialProps = {
    queryKey: getAgentBrandMapKey,
    queryFn: async () => {
      const agentBrandMap: { [key: string]: IBrand[] } = {};

      brands?.brands?.forEach((brand) => {
        brand.agents?.forEach((agentId) => {
          if (!agentBrandMap[agentId]) {
            agentBrandMap[agentId] = [];
          }
          agentBrandMap[agentId].push(brand);
        });
      });

      return agentBrandMap;
    },
    enabled: isBrandsSuccess,
  };

  const finalProps = props ? { ...initialProps, ...props } : initialProps;

  return useApi<{ [key: string]: IBrand[] }, Error>(finalProps);
};

export const fetchAgentLogs = (
  props: Omit<UseQueryOptions, 'queryKey' | 'queryFn'> & {
    agentId: string;
    account: string;
    month: string;
    year: string;
    offset?: number;
  }
) => {
  const { agentId, account, month, year, offset, ...queryProps } = props;

  const initialProps = {
    queryKey: api.getAgentLogsQueryKey(agentId, account, month, year, offset),
    queryFn: () => api.getAgentLogs(agentId, account, month, year, offset),
  };

  const finalProps = queryProps
    ? { ...initialProps, ...queryProps }
    : initialProps;

  return useApi(finalProps);
};

export const fetchMismatchRequests = (
  props: Omit<UseQueryOptions, 'queryKey' | 'queryFn'> & {
    agentId: string;
    account: string;
    date: string;
  }
) => {
  const { agentId, account, date, ...queryProps } = props;

  const initialProps = {
    queryKey: api.getMismatchRequestsQueryKey(agentId, account, date),
    queryFn: () => api.getMismatchRequests(agentId, account, date),
  };

  const finalProps = queryProps
    ? { ...initialProps, ...queryProps }
    : initialProps;

  return useApi(finalProps);
};

export const fetchAllMismatchRequests = (
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>
) => {
  const initialProps = {
    queryKey: api.getAllMismatchRequestsQueryKey,
    queryFn: () => api.getAllMismatchRequests(),
  };

  const finalProps = props ? { ...initialProps, ...props } : initialProps;

  return useApi(finalProps);
};

export const fetchAgentLogById = (
  props: Omit<UseQueryOptions, 'queryKey' | 'queryFn'> & {
    id: string;
  }
) => {
  const { id, ...queryProps } = props;

  const initialProps = {
    queryKey: api.getAgentLogByIdQueryKey(id),
    queryFn: () => api.getAgentLogById(id),
  };

  const finalProps = queryProps
    ? { ...initialProps, ...queryProps }
    : initialProps;

  return useApi(finalProps);
};

export const fetchMultipleAgentLogs = async (
  selectedAgents: any[],
  month: string,
  year: string
) => {
  try {
    // Get brands data
    const brandsResponse = await api.getBrandList();
    if (!brandsResponse.success) {
      throw new Error('Failed to fetch brands');
    }
    const allBrands = brandsResponse.brands || [];

    const agentLogPromises = selectedAgents.map(async (agent) => {
      // Find all brands this agent belongs to.
      const agentBrands = allBrands.filter((brand) =>
        brand.agents?.includes(agent.kimId)
      );

      if (agentBrands.length === 0) {
        toast.error(`No brand found for agent ${agent.name || agent.kimId}`);
        return { agentId: agent._id, success: true, data: [] }; // Return success with empty data
      }

      let combinedLogs: any[] = [];
      let success = true;

      for (const brand of agentBrands) {
        const timeZone = agent.brands?.find(
          (b: any) => b.brandId === brand._id
        )?.timezone;

        const offset = timeZone ? moment.tz(timeZone).utcOffset() : undefined;

        try {
          const logsResponse = await api.getAgentLogs(
            agent.kimId,
            brand.alias || '',
            month,
            year,
            offset
          );
          if (logsResponse.success && logsResponse.data) {
            const logsWithBrand = logsResponse.data.map((log) => ({
              ...log,
              brandName: brand.name,
              timeZone: timeZone || 'UTC',
            }));
            combinedLogs = combinedLogs.concat(logsWithBrand);
          } else {
            console.error(
              `Failed to fetch logs for agent ${agent.name} in brand ${brand.name}`
            );
          }
        } catch (error) {
          success = false;
          console.error(
            `Failed to fetch logs for agent ${agent.name} in brand ${brand.name}:`,
            error
          );
        }
      }
      return { agentId: agent._id, success, data: combinedLogs };
    });

    const results = await Promise.all(agentLogPromises);
    const resultsByAgentId = results.reduce(
      (acc, result) => {
        acc[result.agentId] = { success: result.success, data: result.data };
        return acc;
      },
      {} as { [key: string]: { success: boolean; data: any[] } }
    );

    return { success: true, data: resultsByAgentId };
  } catch (error) {
    console.error('Error fetching multiple agent logs:', error);
    return { success: false, error };
  }
};

export const fetchInvoices = (
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'> & {
    invoiceFor: 'client' | 'agent';
    startDate?: string;
    endDate?: string;
    status?: string;
    paymentStatus?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    clientId?: string;
    agentId?: string;
  }
) => {
  const {
    invoiceFor,
    startDate,
    endDate,
    status,
    paymentStatus,
    search,
    page = 1,
    limit = 10,
    sortBy = 'billDate',
    sortOrder = 'desc',
    clientId,
    agentId,
    ...queryProps
  } = props || {};

  const initialProps = {
    queryKey: [
      ...invoicesQueryKey,
      invoiceFor,
      startDate,
      endDate,
      status,
      paymentStatus,
      search,
      page,
      limit,
      sortBy,
      sortOrder,
      clientId,
      agentId,
    ],
    queryFn: () =>
      getInvoices({
        invoiceFor: invoiceFor!,
        startDate,
        endDate,
        status,
        paymentStatus,
        search,
        page,
        limit,
        sortBy,
        sortOrder,
        clientId,
        agentId,
      }),
    enabled: !!invoiceFor,
  };

  const finalProps = queryProps
    ? { ...initialProps, ...queryProps }
    : initialProps;

  return useApi(finalProps);
};

export const fetchLeaveRequests = (
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'> & {
    status?: string;
    brandId?: string;
    kimId?: string;
  }
) => {
  const { status, brandId, kimId, ...queryProps } = props || {};

  const initialProps = {
    queryKey: getLeaveRequestsQueryKey(status, brandId, kimId),
    queryFn: () => getLeaveRequests({ status, brandId, kimId }),
  };

  const finalProps = queryProps
    ? { ...initialProps, ...queryProps }
    : initialProps;

  return useApi(finalProps);
};

export const fetchAgentLeaveBalance = (
  email: string,
  account: string,
  props?: Omit<UseQueryOptions, 'queryKey' | 'queryFn'>
) => {
  return useApi({
    queryKey: agentLeaveBalanceQueryKey(email, account),
    queryFn: () => getAgentLeaveBalance(email, account),
    enabled: !!email && !!account,
    ...props,
  });
};
